import { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  Granularity,
  OBSERVATIONS_DATA_INTERVAL,
  MAX_DAYS_BY_GRANULARITY,
  ReportType,
} from '../Constants/HistoricalReportConstants';
import { HistoricalReportSettings } from '../types/HistoricalReport';

interface UseReportValidationProps {
  view: string;
  selectedGranularity: any;
  selectedStations: number[];
  selectedLocations: string[];
  hardwareList: any[] | undefined;
  settings: HistoricalReportSettings;
  dateRangeState: {
    startDate: Moment | null;
    endDate: Moment | null;
    startTime: Moment | null;
    endTime: Moment | null;
    daysOfWeek: number[];
  };
  fileName: string;
}

export interface ValidationState {
  isDateRangeValid: boolean;
  isPrecipValid: boolean;
}

export const useReportValidation = ({
  view,
  selectedGranularity,
  selectedStations,
  selectedLocations,
  hardwareList,
  settings,
  dateRangeState,
  fileName,
}: UseReportValidationProps) => {
  const [validations, setValidations] = useState<ValidationState>({ isDateRangeValid: true, isPrecipValid: true });

  useEffect(() => {
    // Get selected hardware objects
    const selectedHardware = hardwareList?.filter(hw => selectedStations.includes(parseInt(hw.id)));

    // Check if any of the selected hardware is not a JBoard
    const isLegacyUnitSelected = selectedHardware?.some(hw => hw.webRelayType !== 3);

    if (isLegacyUnitSelected && selectedGranularity !== Granularity.OneDay) {
      setValidations({ ...validations, isPrecipValid: false });
    } else {
      setValidations({ ...validations, isPrecipValid: true });
    }
  }, [selectedGranularity, selectedStations, hardwareList, setValidations]);

  const showPrecipError = useMemo(() => {
    return !validations.isPrecipValid && (settings.weatherObservations.get('Precipitation')?.length ?? 0) > 0;
  }, [validations.isPrecipValid, settings.weatherObservations]);

  const commonValidations =
    dateRangeState.startDate && dateRangeState.endDate && validations.isDateRangeValid && fileName;
  const weatherConditionValidations =
    settings.weatherObservations.size > 0 && selectedStations.length > 0 && !showPrecipError;
  const policyDelayValidations = settings.policies.size > 0 && selectedLocations.length > 0;
  //   Object.values(settings.formats).includes(true)

  const isExportEnabled = (() => {
    switch (view) {
      case ReportType.WeatherConditions:
        return commonValidations && weatherConditionValidations;
      case ReportType.PolicyDelays:
        return commonValidations && policyDelayValidations;
      default:
        return false;
    }
  })();

  const validateDateRange = (dataInterval: string, startDate: Moment | null, endDate: Moment | null) => {
    const granularity = OBSERVATIONS_DATA_INTERVAL[dataInterval];

    if (!endDate) setValidations({ ...validations, isDateRangeValid: true });
    if (!granularity || !startDate) return;

    const maxPastDays = MAX_DAYS_BY_GRANULARITY[granularity];
    const minDate = moment(endDate).subtract(maxPastDays, 'days');

    if (startDate && startDate.isSameOrBefore(minDate)) {
      setValidations({ ...validations, isDateRangeValid: false });
    } else setValidations({ ...validations, isDateRangeValid: true });
  };

  const validateView = (view: string, dataInterval: string, startDate: Moment | null, endDate: Moment | null) => {
    switch (view) {
      case ReportType.WeatherConditions:
        validateDateRange(dataInterval, startDate, endDate);
        break;
      case ReportType.PolicyDelays:
        setValidations({ ...validations, isDateRangeValid: true });
        break;
      default:
        setValidations({ ...validations, isDateRangeValid: true });
    }
  };

  const updateValidation = (updates: Partial<ValidationState>) => {
    setValidations(prev => ({ ...prev, ...updates }));
  };

  return {
    validations,
    isExportEnabled,
    showPrecipError,
    validateView,
    validateDateRange,
    updateValidation,
  };
};
